import Link from "next/link";
import styles from "./마켓봄_장점_리드_Mobile.module.css";
import type {
  DefaultFormType,
  Form,
  InValidForm,
  ValidForm,
} from "./util/FormType";
import { R, pipe } from "@mobily/ts-belt";
import { match } from "ts-pattern";
import {
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
  useContext,
  useEffect,
} from "react";
import { Env } from "./Env";
import { datadogRum } from "@datadog/browser-rum-slim";
import { FormField } from "./FormField";
import { useRouter } from "next/router";

import Image from "next/image";
import CheckedIcon from "/public/assets/pc/images/icon/icon_checked_white.svg";
import UnCheckedIcon from "/public/assets/pc/images/icon/icon_unchecked_white.svg";

import type { 마켓봄피쳐 } from "./데이터/마켓봄피쳐";
import { 유통서비스가_어려운_이유 } from "./유통서비스의_어려움_Desktop";
import { CTA } from "./CTA_도입문의";
import { asx } from "./util/applyStyle";
import { useTracker } from "./util/Tracker";
import { 링크 } from "./데이터/링크";
import { GrowthBookCTX, getValue } from "./util/GrowthBookCTX";

type DemoShortFormRequiredField = "name" | "phoneNumber";
type DemoShortFormState = Form<DemoShortFormRequiredField, undefined, true>;
type DemoShortFormDefaultState = DefaultFormType<
  DemoShortFormRequiredField,
  undefined,
  true
>;
type ValidDemoShortForm = ValidForm<
  DemoShortFormRequiredField,
  undefined,
  true
>;
type InValidDemoShortForm = InValidForm<
  DemoShortFormRequiredField,
  undefined,
  true
>;
const defaultForm: DemoShortFormDefaultState = {
  name: R.Error(""),
  phoneNumber: R.Error(""),
  privacyAgree: true,
  __state: "error",
};

const as = asx(styles);

const makeFormState = (
  name: R.Result<string, string>,
  phoneNumber: R.Result<string, string>,
  privacyAgree: boolean
): DemoShortFormState => {
  return match([R.isOk(name), R.isOk(phoneNumber), privacyAgree])
    .returnType<DemoShortFormState>()
    .with([true, true, true], () => ({
      name: name,
      phoneNumber: phoneNumber,
      privacyAgree,
      __state: "ok",
    }))
    .otherwise(() => {
      return {
        name,
        phoneNumber,
        privacyAgree,
        __state: "error",
      } satisfies InValidDemoShortForm;
    });
};

type 리드_FormProps = {
  기존유통서비스의불편한점: readonly 마켓봄피쳐[];
  setLeadSent: Dispatch<SetStateAction<boolean>>;
};
const 리드_Form = ({
  기존유통서비스의불편한점,
  setLeadSent,
}: 리드_FormProps) => {
  const isLoading = useRef(false);
  const featureFlag = useContext(GrowthBookCTX);
  const simplifiedRegister = pipe(featureFlag, getValue("simplified-register"));
  const router = useRouter();

  const tracker = useTracker();
  const [formValues, setFormValues] = useState<DemoShortFormState>(defaultForm);

  const apply = (formValues: ValidDemoShortForm) => {
    if (isLoading.current) {
      return;
    }
    const body = new URLSearchParams();
    body.append("name", formValues.name._0);
    body.append("phoneNumber", formValues.phoneNumber._0);
    if (router.query["no"]) {
      body.append("recommendPhoneNumber", atob(router.query["no"]?.toString()));
    }
    body.append(
      "cons",
      JSON.stringify(
        기존유통서비스의불편한점.map(
          (피쳐) =>
            유통서비스가_어려운_이유[피쳐]?.content.replaceAll("\n", " ") ?? ""
        )
      )
    );
    isLoading.current = true;
    fetch(router.query["no"] ? "/api/demoShortEx" : "/api/demoShort", {
      method: "POST",
      body,
    })
      .then((response) => {
        if (response.ok) {
          return response.json();
        } else {
          throw new Error(
            "현재 상담 접수가 어려워 전화를 주시면 빠른 안내 해드리겠습니다."
          );
        }
      })
      .then(
        () => {
          tracker.trackingClick({
            depth1: "LEAD2",
            depth2: "도입문의하기",
            indicator: "리드 전송 시도",
          });
          setLeadSent(true);
        },
        (error) => {
          if (Env.DATADOG_ENABLED) {
            datadogRum.addAction("리드수집오류", {
              type: "데모신청",
              ...formValues,
            });
          }
          alert(error.message);
        }
      )
      .finally(() => {
        isLoading.current = false;
      });
  };

  return (
    <>
      <div className={as("form")}>
        <div className={as("formSection")}>
          <FormField
            label="성함을 알려주세요"
            errorLabel=""
            fieldName="name"
            validateFunction="validName"
            onUpdate={(newValue) => {
              setFormValues((prev) =>
                makeFormState(newValue, prev.phoneNumber, prev.privacyAgree)
              );
            }}
            placeholder="성함"
            styles={styles}
          />
        </div>
        <div className={as("formSection")}>
          <FormField
            className={as("input")}
            fieldName="phoneNumber"
            label="어디로 연락드릴까요?"
            errorLabel=""
            validateFunction={"phone"}
            placeholder={"010-0000-0000"}
            onUpdate={(newValue) => {
              setFormValues((prev) =>
                makeFormState(prev.name, newValue, prev.privacyAgree)
              );
            }}
            styles={styles}
          />
        </div>
        <div className={as("field-privacy")}>
          <div
            className={as("privacy-agree")}
            id="privacyAgreeField"
            onClick={() => {
              setFormValues((prev) =>
                makeFormState(prev.name, prev.phoneNumber, !prev.privacyAgree)
              );
            }}
          >
            <Image
              src={formValues.privacyAgree ? CheckedIcon : UnCheckedIcon}
              alt={
                formValues.privacyAgree
                  ? "개인정보 이용 및 수집 동의함"
                  : "개인정보 이용 및 수집 동의하지 않음"
              }
              width={32}
              height={32}
            />
            <div>개인정보 이용 및 수집 동의</div>
          </div>
          <Link
            className={as("privacy-link")}
            href="https://marketbom.com/term/privacy"
            target="_blank"
          >
            [상세보기]
          </Link>
        </div>
      </div>
      <div className={as("bottom")}>
        <CTA
          onClick={() => {
            match(formValues)
              .with({ __state: "error" }, () => {})
              .with({ __state: "ok" }, apply);
          }}
          activated={formValues.__state === "ok"}
        />

        <Link
          href={
            simplifiedRegister
              ? `${링크.마켓봄_간편가입_실험_링크_PC}?hp=${formValues.phoneNumber._0}`
              : `${링크.마켓봄_간편가입_모바일}&user_name=${formValues.name._0}&phone_number=${formValues.phoneNumber._0}`
          }
          className={as("tryNow")}
          onClick={() => {
            tracker.trackingClick({
              depth1: "LEAD2",
              depth2: "마켓봄",
              indicator: "회원가입",
            });
          }}
        >
          지금 바로 체험해 볼래요.
        </Link>
      </div>
    </>
  );
};

const 리드_제출_완료 = () => {
  const tracker = useTracker();

  return (
    <div className={as("completed")}>
      <div className={as("head")}>{"곧 연락드릴게요."}</div>
      <div className={as("message")}>
        {
          "남겨주신 연락처로 마켓봄 유통전문 컨설턴트가\n빠르게 연락을 드릴 예정입니다."
        }
      </div>
      <Link
        onClick={() => {
          tracker.trackingClick({
            depth1: "LEAD2",
            depth2: "홈으로가기",
          });
        }}
        href={"/"}
        className={as("rewrite")}
      >
        <span>{"홈으로 돌아가기"}</span>
      </Link>
    </div>
  );
};

type 마켓봄_장점_리드_Props = {
  선택된_목록: readonly 마켓봄피쳐[];
};

export const 마켓봄_장점_리드_Mobile = ({
  선택된_목록,
}: 마켓봄_장점_리드_Props) => {
  const [leadSent, setLeadSent] = useState(false);
  const router = useRouter();

  useEffect(() => {
    if (router.query["no"]) {
      fetch(`${Env.BSS_ENTRYPOINT}/public/partner-company`, {
        method: "POST",
        headers: {
          hash: "$2b$10$ehYj3E9rJH7S1KuWwwF3xOdh/d13XPmkIfJgqpWBrxMsx/mggk2JC",
          api_identify_email: "marketbom",
        },
        body: new URLSearchParams({
          path: "GATE_FEATURE",
          classification: "LINKEDIN_PROMOTION",
          requestUserTelNo: atob(router.query["no"]?.toString()),
        }),
      });
    }
  }, [router]);

  return (
    <div className={as("마켓봄_장점_리드")}>
      <div className={as("title")}>{"마켓봄 도입 문의"}</div>
      {leadSent ? (
        <리드_제출_완료 />
      ) : (
        <리드_Form
          기존유통서비스의불편한점={선택된_목록}
          setLeadSent={setLeadSent}
        />
      )}
    </div>
  );
};
